if (!customElements.get('ctr-intro-variants-dropdown')) {
  customElements.define(
    'ctr-intro-variants-dropdown',
    class CtrIntroVariantsDropdown extends HTMLElement {
      constructor() {
        super();
        this.trigger = this.querySelector('.ctr-variants-dropdown__trigger');
        this.triggerInner = this.querySelector('.ctr-variants-dropdown__trigger-inner');
        this.panel = this.querySelector('.ctr-variants-dropdown__panel');
        this.options = [];

        if (!this.trigger || !this.panel || !this.triggerInner) return;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.onOptionClick = this.onOptionClick.bind(this);

        this.trigger.addEventListener('click', this.onTriggerClick);
        this.addEventListener('keydown', this.onKeyDown);
        this.addEventListener('focusout', this.onFocusOut);
        this.panel.addEventListener('click', this.onOptionClick);
      }

      connectedCallback() {
        this.refreshOptions();
        this.cacheSectionTargets();
        this.preloadGalleryImages();
        this.syncActiveOfferSummary();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      cacheSectionTargets() {
        const section = this.closest('.ctr-buy-box');
        if (!section) return;

        this.section = section;
        this.cta = section.querySelector('[data-ctr-buy-box-cta]');
        this.mainImage = section.querySelector('[data-ctr-buy-box-gallery-main]');
        this.thumbImage = section.querySelector('[data-ctr-buy-box-gallery-thumb]');
        this.defaultGallerySrc = section.dataset.defaultGallerySrc || '';
        this.defaultGalleryThumb = section.dataset.defaultGalleryThumb || '';
        this.defaultGalleryAlt = section.dataset.defaultGalleryAlt || '';

        this.offerSummary = section.querySelector('.pdp-offer-summary');
        this.offerSummaryItemName = this.offerSummary?.querySelector('[data-offer-summary-item-name]') || null;
        this.offerSummaryDescriptor = this.offerSummary?.querySelector('[data-offer-summary-descriptor]') || null;
        this.defaultOfferItemName = this.offerSummary?.dataset.defaultOfferItemName || '';
        this.defaultOfferSummaryDescriptor = this.offerSummary?.dataset.defaultOfferSummaryDescriptor || '';
      }

      preloadGalleryImages() {
        const urls = new Set();

        if (this.defaultGallerySrc) urls.add(this.defaultGallerySrc);
        if (this.defaultGalleryThumb) urls.add(this.defaultGalleryThumb);

        this.querySelectorAll('[data-gallery-src]').forEach((option) => {
          const src = option.dataset.gallerySrc;
          const thumb = option.dataset.galleryThumb;
          if (src) urls.add(src);
          if (thumb) urls.add(thumb);
        });

        urls.forEach((url) => {
          const img = new Image();
          img.src = url;
        });
      }

      refreshOptions() {
        this.options = Array.from(this.panel.querySelectorAll('button[role="option"]'));
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      open() {
        this.refreshOptions();
        this.panel.removeAttribute('hidden');
        this.trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocumentClick);
      }

      close({ returnFocus = false } = {}) {
        this.panel.setAttribute('hidden', '');
        this.trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);

        if (returnFocus) {
          this.trigger.focus();
        }
      }

      onTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      }

      onOptionClick(event) {
        const option = event.target.closest('button[role="option"]');
        if (!option || !this.panel.contains(option)) return;

        event.preventDefault();
        this.selectOption(option);
        this.close({ returnFocus: true });
      }

      selectOption(option) {
        this.options.forEach((item) => {
          item.classList.remove('ctr-variants-dropdown__item--active');
          item.removeAttribute('aria-selected');
        });

        option.classList.add('ctr-variants-dropdown__item--active');
        option.setAttribute('aria-selected', 'true');

        const itemInner = option.querySelector('.ctr-variants-dropdown__item-inner');
        if (itemInner) {
          this.triggerInner.innerHTML = `<span class="ctr-variants-dropdown__item">${itemInner.outerHTML}</span>`;
        }

        this.updateCta(option);
        this.updateGallery(option);
        this.updateOfferSummary(option);
        this.slideToFirst();
      }

      updateCta(option) {
        if (!this.cta) return;

        const checkoutUrl = option.dataset.checkoutUrl;
        if (checkoutUrl) {
          this.cta.href = checkoutUrl;
        }
      }

      updateGallery(option) {
        const gallerySrc = option.dataset.gallerySrc || this.defaultGallerySrc;
        const galleryThumb = option.dataset.galleryThumb || this.defaultGalleryThumb;
        const galleryAlt = option.dataset.galleryAlt || this.defaultGalleryAlt;

        if (this.mainImage && gallerySrc) {
          this.mainImage.src = gallerySrc;
          this.mainImage.srcset = '';
          if (galleryAlt) {
            this.mainImage.alt = galleryAlt;
          }

          const gallery = this.section?.buyBoxGallery;
          if (gallery?.mainSlider) {
            const updateHeight = () => gallery.mainSlider.updateAutoHeight(300);
            if (this.mainImage.complete) {
              updateHeight();
            } else {
              this.mainImage.addEventListener('load', updateHeight, { once: true });
            }
          }
        }

        if (this.thumbImage && galleryThumb) {
          this.thumbImage.src = galleryThumb;
          if (galleryAlt) {
            this.thumbImage.alt = galleryAlt;
          }
        }
      }

      updateOfferSummary(option) {
        if (!this.offerSummary) return;

        if (this.offerSummaryItemName) {
          this.offerSummaryItemName.textContent =
            option.dataset.offerSummaryName || this.defaultOfferItemName;
        }

        if (this.offerSummaryDescriptor) {
          this.offerSummaryDescriptor.textContent =
            option.dataset.offerSummaryDescription || this.defaultOfferSummaryDescriptor;
        }
      }

      syncActiveOfferSummary() {
        const activeOption = this.panel.querySelector('button[role="option"][aria-selected="true"]')
          || this.panel.querySelector('button[role="option"].ctr-variants-dropdown__item--active');

        if (activeOption) {
          this.updateOfferSummary(activeOption);
        }
      }

      slideToFirst() {
        const gallery = this.section?.buyBoxGallery;
        if (gallery?.mainSlider) {
          gallery.mainSlider.slideTo(0);
        }
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.close();
        }
      }

      onFocusOut(event) {
        if (!this.isOpen) return;

        const nextTarget = event.relatedTarget;
        if (nextTarget && this.contains(nextTarget)) return;

        this.close();
      }

      onKeyDown(event) {
        if (!this.isOpen && event.target !== this.trigger) return;

        const focusableOptions = this.options;
        const focusedIndex = focusableOptions.findIndex((option) => option === document.activeElement);

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (!this.isOpen) return;
            event.preventDefault();
            this.close({ returnFocus: true });
            break;
          case 'ARROWDOWN':
            if (!this.isOpen) return;
            event.preventDefault();
            this.focusOption(focusableOptions, focusedIndex + 1);
            break;
          case 'ARROWUP':
            if (!this.isOpen) return;
            event.preventDefault();
            this.focusOption(focusableOptions, focusedIndex - 1);
            break;
          case 'ENTER':
          case 'SPACE':
            if (event.target === this.trigger) {
              event.preventDefault();
              if (this.isOpen) {
                this.close();
              } else {
                this.open();
              }
              break;
            }
            if (focusableOptions.includes(event.target)) {
              event.preventDefault();
              this.selectOption(event.target);
              this.close({ returnFocus: true });
            }
            break;
          default:
            break;
        }
      }

      focusOption(options, index) {
        if (!options.length) return;

        const normalizedIndex = ((index % options.length) + options.length) % options.length;
        options[normalizedIndex].focus();
      }
    }
  );
}
